import { getGlobalSettings } from './globalSettings';

export interface KeyboardShortcut {
  id: string;
  key: string;
  description: string;
  handler: () => void;
}

class KeyboardManager {
  private shortcuts: Map<string, KeyboardShortcut> = new Map();
  private isListening: boolean = false;

  constructor() {
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  // 启动键盘监听
  startListening(): void {
    if (this.isListening) return;
    
    document.addEventListener('keydown', this.handleKeyDown);
    this.isListening = true;
  }

  // 停止键盘监听
  stopListening(): void {
    if (!this.isListening) return;
    
    document.removeEventListener('keydown', this.handleKeyDown);
    this.isListening = false;
  }

  // 注册快捷键
  registerShortcut(shortcut: KeyboardShortcut): void {
    this.shortcuts.set(shortcut.key, shortcut);
  }

  // 取消注册快捷键
  unregisterShortcut(key: string): void {
    this.shortcuts.delete(key);
  }

  // 清除所有快捷键
  clearShortcuts(): void {
    this.shortcuts.clear();
  }

  // 获取所有已注册的快捷键
  getShortcuts(): KeyboardShortcut[] {
    return Array.from(this.shortcuts.values());
  }

  // 处理键盘事件
  private handleKeyDown(event: KeyboardEvent): void {
    // 忽略在输入框中的按键
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    // 构建按键组合字符串
    const keyCombo = this.buildKeyCombo(event);
    
    // 查找匹配的快捷键
    const shortcut = this.shortcuts.get(keyCombo);
    if (shortcut) {
      event.preventDefault();
      event.stopPropagation();
      shortcut.handler();
    }
  }

  // 构建按键组合字符串
  private buildKeyCombo(event: KeyboardEvent): string {
    const parts: string[] = [];
    
    if (event.ctrlKey) parts.push('Ctrl');
    if (event.altKey) parts.push('Alt');
    if (event.shiftKey) parts.push('Shift');
    if (event.metaKey) parts.push('Meta');
    
    // 处理特殊键
    let key = event.code;
    if (event.key.length === 1) {
      key = `Key${event.key.toUpperCase()}`;
    }
    
    parts.push(key);
    
    return parts.join('+');
  }

  // 解析快捷键字符串
  parseShortcutString(shortcutString: string): string {
    // 将用户友好的快捷键字符串转换为内部格式
    // 例如: "Ctrl+I" -> "Ctrl+KeyI"
    const parts = shortcutString.split('+');
    const lastPart = parts[parts.length - 1];
    
    // 如果最后一部分是单个字母，转换为KeyCode格式
    if (lastPart.length === 1 && /[A-Za-z]/.test(lastPart)) {
      parts[parts.length - 1] = `Key${lastPart.toUpperCase()}`;
    }
    
    return parts.join('+');
  }

  // 将内部格式转换为用户友好的字符串
  formatShortcutString(keyCombo: string): string {
    // 例如: "Ctrl+KeyI" -> "Ctrl+I"
    return keyCombo.replace(/Key([A-Z])/g, '$1');
  }
}

// 创建全局实例
export const keyboardManager = new KeyboardManager();

// 快捷键常量
export const SHORTCUTS = {
  OPEN_PROMPT_SELECTOR: 'OPEN_PROMPT_SELECTOR',
};

// 初始化默认快捷键
export const initializeDefaultShortcuts = async (onOpenPromptSelector: () => void): Promise<void> => {
  try {
    const settings = await getGlobalSettings();
    const shortcutKey = keyboardManager.parseShortcutString(settings.keyboardShortcut);
    
    keyboardManager.registerShortcut({
      id: SHORTCUTS.OPEN_PROMPT_SELECTOR,
      key: shortcutKey,
      description: '打开提示词选择器',
      handler: onOpenPromptSelector,
    });
    
    keyboardManager.startListening();
  } catch (error) {
    console.error('Failed to initialize keyboard shortcuts:', error);
  }
};

// 更新快捷键设置
export const updateShortcutKey = async (shortcutId: string, newKey: string, handler: () => void): Promise<void> => {
  try {
    keyboardManager.unregisterShortcut(keyboardManager.parseShortcutString(newKey));
    
    const parsedKey = keyboardManager.parseShortcutString(newKey);
    keyboardManager.registerShortcut({
      id: shortcutId,
      key: parsedKey,
      description: '打开提示词选择器',
      handler,
    });
  } catch (error) {
    console.error('Failed to update shortcut key:', error);
  }
};
